<script>
import { useAlert } from 'dashboard/composables';
import AoikumoDisabledTooltip from 'dashboard/components/dahreply/AoikumoDisabledTooltip.vue';

export default {
  name: 'AoikumoOutletField',
  components: { AoikumoDisabledTooltip },
  props: {
    branchId: {
      type: [String, Number],
      required: true,
    },
    branchName: {
      type: String,
      default: '',
    },
    // `{ aoikumo: { integration_id, integration_name, external_id } }` or null.
    providerLinks: {
      type: Object,
      default: null,
    },
  },
  emits: ['updated'],
  data() {
    return {
      selectedIntegrationId: null,
      outletInput: '',
      isSaving: false,
      // { status, code, message } from the last failed save, kept on screen so
      // it can be quoted when reporting an issue.
      error: null,
      showUnlinkConfirm: false,
      showMoveConfirm: false,
    };
  },
  computed: {
    isConnected() {
      return this.$store.getters['aoikumo/isConnected'];
    },
    isAdmin() {
      return this.$store.getters.getCurrentRole === 'administrator';
    },
    integrations() {
      return this.$store.getters['aoikumo/getIntegrations'];
    },
    hasMultipleIntegrations() {
      return this.$store.getters['aoikumo/hasMultipleIntegrations'];
    },
    currentLink() {
      return this.providerLinks?.aoikumo || null;
    },
    linkedOutlet() {
      return this.currentLink?.external_id || null;
    },
    linkedIntegrationId() {
      return this.currentLink?.integration_id ?? null;
    },
    linkedIntegrationName() {
      return (
        this.currentLink?.integration_name ||
        this.$store.getters['aoikumo/getIntegrationById'](
          this.linkedIntegrationId
        )?.name ||
        ''
      );
    },
    selectedIntegration() {
      return this.$store.getters['aoikumo/getIntegrationById'](
        this.selectedIntegrationId
      );
    },
    outletValue() {
      return this.outletInput.trim();
    },
    hasChanged() {
      return (
        this.selectedIntegrationId !== this.linkedIntegrationId ||
        this.outletValue !== this.linkedOutlet
      );
    },
    canSave() {
      return (
        this.isAdmin &&
        !this.isSaving &&
        this.selectedIntegrationId !== null &&
        this.outletValue.length > 0 &&
        this.hasChanged
      );
    },
    adminHint() {
      return this.$t('AOIKUMO_OUTLET.ADMIN_HINT');
    },
    isUnreachable() {
      return this.error && this.error.code === 'unreachable';
    },
    errorText() {
      if (!this.error) return '';
      const text =
        this.error.message || this.$t('AOIKUMO_OUTLET.ALERTS.GENERIC_ERROR');
      return this.isUnreachable
        ? `${text} ${this.$t('AOIKUMO_OUTLET.TRY_AGAIN')}`
        : text;
    },
    // Modal B: moving an already linked branch to another outlet or integration.
    moveParams() {
      return {
        branch: this.branchName,
        oldName: this.linkedIntegrationName,
        oldOutlet: this.linkedOutlet,
        newName: this.selectedIntegration?.name || '',
        newOutlet: this.outletValue,
      };
    },
  },
  watch: {
    // Follow the saved link; with exactly one integration it is preselected.
    linkedIntegrationId: { handler: 'resetSelection', immediate: true },
    linkedOutlet: { handler: 'resetSelection', immediate: true },
    integrations: 'preselectOnly',
    branchId() {
      this.error = null;
    },
  },
  methods: {
    resetSelection() {
      this.outletInput = this.linkedOutlet || '';
      this.selectedIntegrationId = this.linkedIntegrationId;
      this.preselectOnly();
    },
    preselectOnly() {
      if (
        this.selectedIntegrationId === null &&
        this.integrations.length === 1
      ) {
        this.selectedIntegrationId = this.integrations[0].integration_id;
      }
    },
    async save({ integrationId, outlet }) {
      this.isSaving = true;
      this.error = null;
      try {
        await this.$store.dispatch('aoikumo/setBranchLink', {
          branchId: this.branchId,
          integrationId,
          outlet,
        });
        this.$emit('updated');
        const name = this.selectedIntegration?.name || '';
        useAlert(
          outlet
            ? this.$t(
                this.hasMultipleIntegrations
                  ? 'AOIKUMO_OUTLET.ALERTS.LINKED_NAMED'
                  : 'AOIKUMO_OUTLET.ALERTS.LINKED',
                { outlet, name }
              )
            : this.$t('AOIKUMO_OUTLET.ALERTS.UNLINKED')
        );
      } catch (error) {
        this.error = error || {};
        useAlert(this.errorText);
      } finally {
        this.isSaving = false;
      }
    },
    saveLink() {
      return this.save({
        integrationId: this.selectedIntegrationId,
        outlet: this.outletValue,
      });
    },
    onSave() {
      if (!this.canSave) return;
      // A branch that is already linked is being moved: confirm first.
      if (this.linkedOutlet) {
        this.showMoveConfirm = true;
      } else {
        this.saveLink();
      }
    },
    onConfirmMove() {
      this.showMoveConfirm = false;
      this.saveLink();
    },
    onRemove() {
      if (this.isAdmin && !this.isSaving) this.showUnlinkConfirm = true;
    },
    onConfirmUnlink() {
      this.showUnlinkConfirm = false;
      this.save({ integrationId: null, outlet: null });
    },
  },
};
</script>

<template>
  <div v-if="isConnected" data-testid="aoikumo-outlet-field">
    <p class="text-slate-500 text-sm">
      {{ $t('AOIKUMO_OUTLET.LABEL') }}
    </p>
    <p
      v-if="linkedOutlet"
      class="text-lg font-medium text-slate-900 dark:text-white"
      data-testid="aoikumo-outlet-value"
    >
      {{
        hasMultipleIntegrations
          ? $t('AOIKUMO_OUTLET.LINKED_NAMED', {
              name: linkedIntegrationName,
              outlet: linkedOutlet,
            })
          : $t('AOIKUMO_OUTLET.LINKED', { outlet: linkedOutlet })
      }}
    </p>
    <p
      v-else
      class="text-sm text-yellow-700 dark:text-yellow-300"
      data-testid="aoikumo-outlet-unlinked"
    >
      {{ $t('AOIKUMO_OUTLET.NOT_LINKED') }}
    </p>

    <div class="mt-2">
      <AoikumoDisabledTooltip :disabled="!isAdmin" :message="adminHint">
        <label class="block mb-2">
          <span class="block mb-1 text-xs text-slate-500 dark:text-slate-400">
            {{ $t('AOIKUMO_OUTLET.INTEGRATION_LABEL') }}
          </span>
          <select
            v-model="selectedIntegrationId"
            class="!mb-0 max-w-[16rem]"
            data-testid="aoikumo-outlet-integration"
            :disabled="!isAdmin || isSaving"
          >
            <option :value="null" disabled>
              {{ $t('AOIKUMO_OUTLET.INTEGRATION_PLACEHOLDER') }}
            </option>
            <option
              v-for="item in integrations"
              :key="item.integration_id"
              :value="item.integration_id"
            >
              {{ item.name }}
            </option>
          </select>
        </label>
      </AoikumoDisabledTooltip>
      <div class="flex items-center gap-2">
        <AoikumoDisabledTooltip :disabled="!isAdmin" :message="adminHint">
          <input
            v-model="outletInput"
            type="text"
            class="!mb-0 max-w-[12rem]"
            data-testid="aoikumo-outlet-input"
            :placeholder="$t('AOIKUMO_OUTLET.PLACEHOLDER')"
            :disabled="!isAdmin || isSaving"
            @keyup.enter="onSave"
          />
        </AoikumoDisabledTooltip>
        <AoikumoDisabledTooltip :disabled="!isAdmin" :message="adminHint">
          <woot-button
            size="small"
            color-scheme="primary"
            data-testid="aoikumo-outlet-save"
            :is-loading="isSaving"
            :is-disabled="!canSave"
            @click="onSave"
          >
            {{
              isSaving
                ? $t('AOIKUMO_OUTLET.CHECKING')
                : $t('AOIKUMO_OUTLET.SAVE')
            }}
          </woot-button>
        </AoikumoDisabledTooltip>
        <AoikumoDisabledTooltip
          v-if="linkedOutlet"
          :disabled="!isAdmin"
          :message="adminHint"
        >
          <woot-button
            size="small"
            variant="clear"
            color-scheme="alert"
            data-testid="aoikumo-outlet-remove"
            :is-disabled="!isAdmin || isSaving"
            @click="onRemove"
          >
            {{ $t('AOIKUMO_OUTLET.REMOVE') }}
          </woot-button>
        </AoikumoDisabledTooltip>
      </div>
      <p
        class="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400"
        data-testid="aoikumo-outlet-help"
      >
        {{ $t('AOIKUMO_OUTLET.HELP') }}
      </p>
      <p
        v-if="!isAdmin"
        class="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400"
        data-testid="aoikumo-outlet-admin-hint"
      >
        {{ adminHint }}
      </p>
      <div
        v-if="error"
        class="mt-2 text-xs text-red-600 dark:text-red-400"
        data-testid="aoikumo-outlet-error"
      >
        <p class="mb-0" data-testid="aoikumo-outlet-error-text">
          {{ errorText }}
        </p>
        <p
          v-if="error.status"
          class="mb-0 opacity-80"
          data-testid="aoikumo-outlet-error-status"
        >
          {{ $t('AOIKUMO_OUTLET.REFERENCE', { status: error.status }) }}
        </p>
      </div>
    </div>

    <!-- A. Unlink a branch -->
    <woot-delete-modal
      v-if="showUnlinkConfirm"
      :show.sync="showUnlinkConfirm"
      :on-close="() => (showUnlinkConfirm = false)"
      :on-confirm="onConfirmUnlink"
      :title="
        $t('AOIKUMO_OUTLET.UNLINK_CONFIRM.TITLE', {
          branch: branchName,
          integration: linkedIntegrationName,
        })
      "
      :message="
        $t('AOIKUMO_OUTLET.UNLINK_CONFIRM.MESSAGE', {
          integration: linkedIntegrationName,
        })
      "
      :confirm-text="$t('AOIKUMO_OUTLET.UNLINK_CONFIRM.CONFIRM')"
      :reject-text="$t('AOIKUMO_OUTLET.UNLINK_CONFIRM.CANCEL')"
    />

    <!-- B. Move a branch to another integration or outlet -->
    <woot-delete-modal
      v-if="showMoveConfirm"
      :show.sync="showMoveConfirm"
      :on-close="() => (showMoveConfirm = false)"
      :on-confirm="onConfirmMove"
      :title="$t('AOIKUMO_OUTLET.MOVE_CONFIRM.TITLE', moveParams)"
      :message="$t('AOIKUMO_OUTLET.MOVE_CONFIRM.MESSAGE', moveParams)"
      :confirm-text="$t('AOIKUMO_OUTLET.MOVE_CONFIRM.CONFIRM')"
      :reject-text="$t('AOIKUMO_OUTLET.MOVE_CONFIRM.CANCEL')"
    />
  </div>
</template>
